<script setup lang="ts">
import { Location, MapOptions } from '@/models'
import { computed, useTemplateRef, watch } from 'vue'
import { AdvancedMarker, GoogleMap } from 'vue3-google-map'

const apiKey = import.meta.env.VITE_GOOGLE_MAPS_FRONTEND_API_KEY

defineEmits(['selectLocation'])

const props = defineProps<{
    options: MapOptions
    locations: Array<Location>
}>()

const mapRef = useTemplateRef('map')

const center = { lat: props.options.centerLat, lng: props.options.centerLng }

const markers = computed(() =>
    props.locations.map((location) => {
        return {
            options: {
                position: {
                    lat: location.coordinates.coordinates[1],
                    lng: location.coordinates.coordinates[0],
                },
                title: location.name,
            },
            location: location,
        }
    }),
)

const domParser = new DOMParser()
const cupraLogoSvgString =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 410.57 315.559"><path fill="#fff" d="M324.875 65.153L258.26 97.746l40.605 15.58c8.363 3.18 18.315 7.282 21.972 17.171 3.657 9.89-1.78 19.079-6.646 26.869l-22.258 34.723q-39.46 61.782-78.985 123.47v-38.157q15.136-47.697 29.445-95.393 4.038-13.61 8.013-27.218c1.59-5.47.572-8.745-4.707-11.352l-40.51-19.937-40.35 19.905c-5.279 2.607-6.36 5.882-4.707 11.352q3.943 13.64 8.013 27.218 14.246 47.887 29.445 95.393v38.157q-39.588-61.687-78.985-123.47l-22.258-34.723c-4.897-7.663-10.398-16.725-6.646-26.869 3.752-10.143 13.61-13.959 21.972-17.17l40.605-15.581L85.663 65.12l20.763 34.819-34.341 13.196Q35.995 56.63 0 0c68.365 24.23 136.73 48.682 205.285 72.117C273.935 48.682 342.205 24.262 410.57 0q-35.932 56.568-72.054 113.008l-34.34-13.195 20.763-34.819" /></svg>'
const createCupraSvg = () => domParser.parseFromString(cupraLogoSvgString, 'image/svg+xml').documentElement

const createMarkerPinOptions = () => ({
    background: '#000',
    glyphColor: '#fff',
    borderColor: 'transparent',
    glyph: createCupraSvg(),
    scale: 1.3,
})

watch(
    () => mapRef.value?.ready,
    (ready) => {
        if (!ready || !mapRef.value || !mapRef.value.map || !mapRef.value.api) {
            return
        }
        const bounds = new mapRef.value.api.LatLngBounds(center)
        markers.value.forEach((marker) => {
            const latLng = new google.maps.LatLng(marker.options.position)
            bounds.extend(latLng)
        })
        mapRef.value.map.fitBounds(bounds)
    },
)
</script>

<template>
    <GoogleMap
        ref="map"
        :api-key="apiKey"
        mapId="DEMO_MAP_ID"
        :center="center"
        :zoom="6"
    >
        <AdvancedMarker
            v-for="(marker, ix) in markers"
            :key="ix"
            :options="marker.options"
            :pin-options="createMarkerPinOptions()"
            @click="$emit('selectLocation', marker.location)"
        />
    </GoogleMap>
</template>
